import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

type MermaidState = 'loading' | 'rendered' | 'failed';

/**
 * Mermaid is a large dependency and most messages contain no diagram, so it is
 * lazy-loaded on first use and the initialized instance is shared across every
 * diagram in the app (mirrors the `import('echarts')` idiom used by
 * CustomGraphModelComponent).
 */
let mermaidInstance: any = null;
let mermaidLoader: Promise<any> | null = null;
let diagramSequence = 0;

/**
 * Rendered SVG keyed by diagram source.
 *
 * MarkdownRenderPipe regenerates the whole message innerHTML on every streaming
 * chunk, which tears down and re-creates this component once per token for any
 * diagram that closes before the end of the message. The cache makes every
 * re-creation after the first a synchronous lookup instead of a fresh layout pass,
 * so the diagram stays put instead of flickering back to the loading state.
 */
const renderedSvgCache = new Map<string, string>();
/** Sources already known to be invalid, so re-creations don't re-render or re-log. */
const failedSourceCache = new Set<string>();
const RENDERED_CACHE_LIMIT = 50;

function evictOldest(cache: Map<string, string> | Set<string>): void {
    if (cache.size < RENDERED_CACHE_LIMIT) return;
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
}

function cacheRenderedSvg(source: string, svg: string): void {
    evictOldest(renderedSvgCache);
    renderedSvgCache.set(source, svg);
}

function cacheFailedSource(source: string): void {
    evictOldest(failedSourceCache);
    failedSourceCache.add(source);
}

@Component({
    selector: 'app-custom-mermaid',
    standalone: true,
    templateUrl: './custom-mermaid.component.html',
    styleUrl: './custom-mermaid.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomMermaidComponent implements OnInit, OnDestroy {
    @Input() public code: string = '';

    protected readonly state = signal<MermaidState>('loading');
    protected readonly svg = signal<SafeHtml | null>(null);

    private readonly renderId = `mermaid-diagram-${++diagramSequence}`;
    private destroyed = false;

    constructor(private sanitizer: DomSanitizer) { }

    ngOnInit() {
        const source = (this.code ?? '').trim();

        // Already seen this diagram — settle synchronously so a re-creation mid-stream
        // never drops back to the loading state or re-runs a doomed render.
        const cached = source ? renderedSvgCache.get(source) : undefined;
        if (cached) {
            this.svg.set(this.sanitizer.bypassSecurityTrustHtml(cached));
            this.state.set('rendered');
            return;
        }

        if (source && failedSourceCache.has(source)) {
            this.state.set('failed');
            return;
        }

        void this.renderDiagram();
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.removeOrphanNodes();
    }

    private loadMermaid(): Promise<any> {
        if (mermaidInstance) return Promise.resolve(mermaidInstance);

        if (!mermaidLoader) {
            mermaidLoader = import('mermaid').then(module => {
                const mermaid = module.default;
                mermaid.initialize({
                    startOnLoad: false,
                    // Diagram content originates from an LLM and can be shaped by database
                    // contents or a crafted prompt, so it is untrusted input rendered as SVG.
                    // 'strict' encodes HTML in labels and disables click bindings — do not
                    // loosen it (see CVE-2026-21866 for what happens otherwise).
                    securityLevel: 'strict',
                    theme: 'default',
                    fontFamily: 'inherit'
                });
                mermaidInstance = mermaid;
                return mermaid;
            });
        }

        return mermaidLoader;
    }

    private async renderDiagram(): Promise<void> {
        const source = (this.code ?? '').trim();
        if (!source) {
            this.state.set('failed');
            return;
        }

        try {
            const mermaid = await this.loadMermaid();

            // Same parser that renders, so this is exact-fidelity validation.
            // suppressErrors makes it resolve false instead of throwing.
            const isValid = await mermaid.parse(source, { suppressErrors: true });
            if (!isValid) {
                throw new Error('Mermaid syntax is not valid');
            }

            const { svg } = await mermaid.render(this.renderId, source);
            cacheRenderedSvg(source, svg);
            if (this.destroyed) return;

            // Mermaid runs its own DOMPurify pass under securityLevel 'strict'; Angular's
            // sanitizer would strip the <style> block and break the diagram's appearance.
            this.svg.set(this.sanitizer.bypassSecurityTrustHtml(svg));
            this.state.set('rendered');
        } catch (error) {
            console.error('[Mermaid] Failed to render diagram:', error, '\nSource:\n', source);
            cacheFailedSource(source);
            this.removeOrphanNodes();
            if (this.destroyed) return;
            this.state.set('failed');
        }
    }

    /** mermaid.render() can leave its temporary working node behind when it throws. */
    private removeOrphanNodes(): void {
        document.getElementById(this.renderId)?.remove();
        document.getElementById(`d${this.renderId}`)?.remove();
    }
}
